import { useEffect, useState } from 'react';
import { isInBrowser } from '../../../common/utils/ssrUtils.js';
import {
  ACTIVE_CHAT_KEY,
  CHAT_STATE_CHANGED_EVENT,
  SURVEY_PENDING_KEY,
  hasActiveChatSession,
  isSurveyPending,
  setActiveChatSession,
} from '../chatSession.js';
import { COOKIE_KEY_MESSAGE_ID } from '../constants.js';

const hasGenesysCookie = (): boolean => {
  if (!isInBrowser()) {
    return false;
  }
  return document.cookie.includes(COOKIE_KEY_MESSAGE_ID);
};

const readSessionState = (): boolean => {
  return hasGenesysCookie() || isSurveyPending() || hasActiveChatSession();
};

export const useGenesysSessionDetection = (enabled: boolean): boolean => {
  const [hasActiveSession, setHasActiveSession] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setHasActiveSession(false);
      return;
    }

    const initState = () => {
      const hasCookie = hasGenesysCookie();
      const surveyPending = isSurveyPending();
      const active = hasCookie || surveyPending;

      if (active && !hasActiveChatSession()) {
        setActiveChatSession();
      }

      setHasActiveSession(active);
    };

    initState();

    if (!isInBrowser()) {
      return;
    }

    const handleStateChange = () => {
      const state = readSessionState();
      setHasActiveSession(state);
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACTIVE_CHAT_KEY || e.key === SURVEY_PENDING_KEY) {
        handleStateChange();
      }
    };

    globalThis.addEventListener('storage', handleStorageChange);
    globalThis.addEventListener(CHAT_STATE_CHANGED_EVENT, handleStateChange);

    return () => {
      globalThis.removeEventListener('storage', handleStorageChange);
      globalThis.removeEventListener(CHAT_STATE_CHANGED_EVENT, handleStateChange);
    };
  }, [enabled]);

  return hasActiveSession;
};
